import React, { useState } from "react";
import useSWR from "swr";

import { emitUpsellModalImpression } from "@catchoftheday/analytics";
import {
  Flex,
  IconOnePass,
  Span,
  SpanProps,
} from "@catchoftheday/cg-components";
import {
  DEFAULT_FREE_TRIAL_DAYS,
  ONEPASS_MONTHLY_PRICE,
  ONEPASS_YEARLY_PRICE,
} from "@catchoftheday/onepass";
import { useTheme } from "@catchoftheday/theme";
import { pluralize } from "@catchoftheday/utilities";

import { fetcher } from "@header/api";
import {
  HeaderTile,
  HeaderTileRenderProps,
} from "@header/components/HeaderTile";
import { SpanWithAfterDownArrow } from "@header/components/SpanWithAfterDownArrow";
import { HEADER_LOYALTY_API } from "@header/consts";
import { AxiosResponse, HeaderLoyalty } from "@header/types";

import { HeaderTileContainer } from "../HeaderTileContainer";

import { PopupContent } from "./PopupContent";
const IconSpan = (props: SpanProps) => (
  <Span fontSize="xs" fontWeight={400} textAlign="center" {...props} />
);

export type OnePassTileProps = {
  isLoggedIn?: boolean;
};

export const OnePassTile: React.FC<OnePassTileProps> = ({ isLoggedIn }) => {
  const theme = useTheme();
  const [isImpressionFired, setIsImpressionFired] = useState(false);
  const { data: loyaltyResponse, isValidating } = useSWR<
    AxiosResponse<HeaderLoyalty>
  >(HEADER_LOYALTY_API, fetcher);

  const membershipData = loyaltyResponse?.data?.membership;
  const clubData = loyaltyResponse?.data?.club;

  const isLinked = membershipData?.linked;
  const hasSubscription =
    membershipData?.subscription?.active || clubData?.active;
  const isFreeTrial = membershipData?.subscription?.onFreeTrial;
  const freeTrialDaysRemaining =
    membershipData?.subscription?.freeTrialDays ?? DEFAULT_FREE_TRIAL_DAYS;
  const monthlyPrice =
    membershipData?.subscription?.monthlyPrice ?? ONEPASS_MONTHLY_PRICE;
  const yearlyPrice =
    membershipData?.subscription?.yearlyPrice ?? ONEPASS_YEARLY_PRICE;
  const hidePopupContent = isLoggedIn && hasSubscription;

  const showFreeTrial =
    !isLoggedIn ||
    !membershipData ||
    (!clubData?.active &&
      !membershipData?.linked &&
      !clubData?.eligibleFreeTrial) ||
    membershipData?.subscription?.freeTrialEligible;
  const showLogIn = !isLoggedIn;
  const showOnePassLink = isLoggedIn && !isLinked;
  const showAlreadyAMember = showLogIn || showOnePassLink;

  const renderOnePassIcon = () => (
    <IconOnePass
      verticalAlign="top"
      height="15px"
      width="72px"
      title="OnePass"
    />
  );

  const renderIconContent = () => {
    if (!hasSubscription) {
      return (
        <>
          <IconSpan mb="4px">{renderOnePassIcon()}</IconSpan>
          <SpanWithAfterDownArrow fontSize="xs">
            Free delivery
          </SpanWithAfterDownArrow>
        </>
      );
    }
    if (isFreeTrial) {
      return (
        <>
          <IconSpan>{renderOnePassIcon()}</IconSpan>
          <IconSpan>
            {`Free trial expires in ${pluralize(
              freeTrialDaysRemaining,
              "day"
            )}`}
          </IconSpan>
        </>
      );
    }

    return (
      <>
        <IconSpan>{renderOnePassIcon()}</IconSpan>
        <IconSpan>Show me exclusive deals!</IconSpan>
      </>
    );
  };

  const getTileLinkProps = () => {
    let ariaLabel = "OnePass shop";
    let role = "link";
    let href: string | undefined = "/shop/onepass";
    if (!hasSubscription) {
      ariaLabel = "Free delivery with OnePass";
      href = undefined;
      role = "button";
    }
    return {
      "aria-label": ariaLabel,
      href,
      role,
    };
  };

  const renderIcon: HeaderTileRenderProps = ({ isOpen }) => {
    const iconStyleOnHover = {
      svg: {
        fill: theme?.colors?.["onePass"]?.["primary"],
      },
    };

    const iconStyle = {
      "&:hover": iconStyleOnHover,
      ...(isOpen && { svg: iconStyleOnHover.svg }),
    };

    return (
      <HeaderTileContainer
        hasRightBorder
        flexProps={{
          px: "10px",
          maxWidth: "130px",
        }}
        linkProps={{
          css: iconStyle,
          backgroundColor: isOpen ? "white" : undefined,
          color: isOpen ? "secondaryDark" : "white",
          className: "js-dnt",
          "data-tracking": "header-app-onepass-tile",
          ...getTileLinkProps(),
        }}
      >
        <Flex flexDirection="column" alignItems="center">
          {renderIconContent()}
        </Flex>
      </HeaderTileContainer>
    );
  };

  if (isValidating) {
    return null;
  }

  return (
    <HeaderTile
      popoverProps={{
        placement: "bottom",
      }}
      icon={renderIcon}
      onTileShow={(isOpen) => {
        if (isOpen && !isImpressionFired) {
          setIsImpressionFired(true);
          emitUpsellModalImpression("Upsell Modal Header");
        }
      }}
    >
      <PopupContent
        hideContent={hidePopupContent || isValidating}
        showFreeTrial={showFreeTrial}
        showLogIn={showLogIn}
        showAlreadyAMember={showAlreadyAMember}
        monthlyPrice={monthlyPrice}
        yearlyPrice={yearlyPrice}
      />
    </HeaderTile>
  );
};
